import React, { useState } from "react";
import "./FacultyProfile.css"; // Make sure this path is correct for your project

const toArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  return [value].filter(Boolean);
};

const formatEntry = (item) => {
  if (!item) return "";
  if (typeof item === "string" || typeof item === "number") return String(item);

  if (Array.isArray(item)) {
    return item.filter(Boolean).join(" | ");
  }

  if (typeof item === "object") {
    const preferredOrder = [
      "title",
      "name",
      "degree",
      "specialization",
      "college",
      "university",
      "organization",
      "role",
      "designation",
      "department",
      "subject",
      "venue",
      "journal",
      "conference",
      "publisher",
      "indexing",
      "year",
      "date",
      "duration",
      "status",
      "isbn",
      "issn",
      "doi",
      "link",
      "description",
    ];

    const keys = [
      ...preferredOrder.filter((key) => item[key]),
      ...Object.keys(item).filter((key) => !preferredOrder.includes(key) && item[key]),
    ];

    return keys
      .map((key) => {
        const value = item[key];
        if (Array.isArray(value)) return value.filter(Boolean).join(", ");
        return value;
      })
      .filter(Boolean)
      .join(" | ");
  }

  return String(item);
};

const renderList = (items, emptyText = "No information available.") => {
  const normalizedItems = toArray(items).map(formatEntry).filter(Boolean);

  if (!normalizedItems.length) {
    return <p className="faculty-empty">{emptyText}</p>;
  }

  return (
    <ul className="faculty-list">
      {normalizedItems.map((item, idx) => (
        <li key={idx}>{item}</li>
      ))}
    </ul>
  );
};

const renderField = (label, value) => {
  if (!value) return null;

  return (
    <div className="faculty-meta-item">
      <span className="faculty-meta-label">{label}</span>
      <span className="faculty-meta-value">{value}</span>
    </div>
  );
};

const renderSubSection = (title, items, emptyText) => (
  <div className="faculty-subdiv">
    {title && <h3 className="faculty-subtitle">{title}</h3>}
    {renderList(items, emptyText)}
  </div>
);

export default function FacultyProfile({ faculty }) {
  // STATE: Track which accordion sections are open
  const [openSections, setOpenSections] = useState({
    intro: true, // Introduction is open by default
    academic: false,
    projects: false,
    research: false,
  });

  // FUNCTION: Toggle a specific accordion section
  const toggleSection = (section) => {
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  if (!faculty) return null;

  const biography = faculty.biography || {};
  const publications = biography.publications || faculty.publications || {};
  const teaching = biography.teaching || faculty.teaching || {};
  const projects = biography.projects || faculty.projects || {};

  const fullName =
    faculty.fullName ||
    faculty.name ||
    [faculty.title, faculty.firstName, faculty.lastName].filter(Boolean).join(" ");

  const title = faculty.title || faculty.salutation || "";
  const designation = faculty.designation || faculty.role || "";
  const department = faculty.department || biography.department || "";
  const officialEmail = faculty.officialEmail || faculty.email || biography.officialEmail || "";
  const introduction = faculty.introduction || faculty.aboutMe || biography.introduction || "";

  const education = biography.education || faculty.education || [];
  const professionalExperience =
    biography.professionalExperience ||
    biography.experience ||
    faculty.professionalExperience ||
    faculty.experience ||
    [];

  const coursesTeaching =
    faculty.coursesTeaching ||
    biography.coursesTeaching ||
    teaching.currentCourses ||
    teaching.previousCourses ||
    [];

  const researchSpecialisation =
    faculty.researchSpecialisation ||
    faculty.researchSpecialization ||
    biography.researchSpecialisation ||
    biography.researchSpecialization ||
    [];

  const consultancyProjects =
    faculty.consultancyProjects ||
    biography.consultancyProjects ||
    biography.consultancy ||
    [];

  const achievementsAwards =
    faculty.achievementsAwards ||
    faculty.honorsAwards ||
    biography.achievementsAwards ||
    biography.honorsAwards ||
    biography.honorsAndAwards ||
    [];

  const sttpMdpFdp =
    faculty.sttpMdpFdp ||
    faculty.fdp ||
    biography.sttpMdpFdp ||
    biography.mdp ||
    biography.fdp ||
    [];

  const researchPapers =
    faculty.researchPapers ||
    publications.researchPapers ||
    publications.papers ||
    [];

  const books =
    faculty.books ||
    publications.books ||
    publications.bookChapters ||
    publications.booksWithInstituteName ||
    [];

  const ipr =
    faculty.ipr ||
    faculty.IPR ||
    biography.ipr ||
    biography.IPR ||
    [];

  const certifications = biography.certifications || faculty.certifications || [];
  const guidedProjects =
    faculty.guidedProjects ||
    projects.guidedProjects ||
    projects.research ||
    [];

  return (
    <div className="faculty-details-container">
      <div className="faculty-grid">
        {/* LEFT SIDEBAR (Profile Info) */}
        <div className="faculty-sidebar">
          <div className="faculty-card">
            <div className="faculty-image-wrapper">
              <img src={faculty.image} alt={`${fullName} profile`} className="faculty-image" />
            </div>

            <div className="faculty-name">{fullName}</div>
            {title ? <div className="faculty-position">{title}</div> : null}
            {designation ? <div className="faculty-position">{designation}</div> : null}

            <div className="faculty-meta">
              {renderField("Department", department)}
              {renderField("Official Email", officialEmail)}
            </div>
          </div>
        </div>

        {/* RIGHT CONTENT (Accordions) */}
        <div className="faculty-details">
          {/* Introduction Section */}
          <div className="faculty-div">
            <div
              className={`faculty-div-title ${openSections.intro ? "open" : ""}`}
              onClick={() => toggleSection("intro")}
            >
              Introduction
            </div>
            {openSections.intro && (
              <div className="faculty-content">
                {renderSubSection("", introduction ? [introduction] : [])}
              </div>
            )}
          </div>

          {/* Academic Profile Section */}
          <div className="faculty-div">
            <div
              className={`faculty-div-title ${openSections.academic ? "open" : ""}`}
              onClick={() => toggleSection("academic")}
            >
              Academic Profile
            </div>
            {openSections.academic && (
              <div className="faculty-content">
                {renderSubSection("Education", education)}
                {renderSubSection("Professional Experience", professionalExperience)}
                {renderSubSection("Courses/Subjects Teaching", coursesTeaching)}
                {renderSubSection("Research Specialisation", researchSpecialisation)}
              </div>
            )}
          </div>

          {/* Projects & Achievements Section */}
          <div className="faculty-div">
            <div
              className={`faculty-div-title ${openSections.projects ? "open" : ""}`}
              onClick={() => toggleSection("projects")}
            >
              Projects & Achievements
            </div>
            {openSections.projects && (
              <div className="faculty-content">
                {renderSubSection("Consultancy Projects", consultancyProjects)}
                {renderSubSection("Achievements/Awards", achievementsAwards)}
                {renderSubSection("Guided Projects", guidedProjects)}
                {renderSubSection("STTP / MDP / FDP", sttpMdpFdp)}
                {renderSubSection("Certifications", certifications)}
              </div>
            )}
          </div>

          {/* Research & Publications Section */}
          <div className="faculty-div">
            <div
              className={`faculty-div-title ${openSections.research ? "open" : ""}`}
              onClick={() => toggleSection("research")}
            >
              Research & Publications
            </div>
            {openSections.research && (
              <div className="faculty-content">
                {renderSubSection("Research Papers", researchPapers)}
                {renderSubSection("Books / Chapters", books)}
                {renderSubSection("IPR", ipr)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}